import { InfoCircleIcon, Link01Icon } from '@langchain/untitled-ui-icons';

import { ReactNode } from 'react';

import { ICON_MAP } from '@/Pages/Settings/utils/ResourceIconMap';
import { Badge, Tooltip } from '@/design-system';
import { ResourceType } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { CopyInlineLink } from './CopyInlineLink';
import { Skeleton } from './Skeleton';
import { TextOverflowTooltip } from './TextOverflowTooltip';

export function PageTitle({
  icon,
  children,
  description,
  contentClassName,
  iconClassName,
  className,
}: {
  children: ReactNode;
  description?: ReactNode;
  icon?: React.ReactElement;
  className?: string;
  contentClassName?: string;
  iconClassName?: string;
}) {
  return (
    <h2
      className={cn(
        'mr-2 grid min-w-0 flex-grow grid-cols-[auto,1fr] items-center truncate py-4 font-medium display-base',
        !icon && 'gap-y-1',
        className
      )}
    >
      {icon ? (
        <div
          className={cn(
            'mr-2 rounded bg-brand-tertiary p-1 text-brand-tertiary',
            iconClassName
          )}
        >
          {icon}
        </div>
      ) : null}

      <span
        className={cn(
          'overflow-hidden text-ellipsis tracking-tighter',
          !icon && 'col-start-2',
          contentClassName
        )}
      >
        {children ?? <span className="opacity-0">...</span>}
      </span>

      {description && (
        <TextOverflowTooltip className="col-start-2 overflow-hidden text-ellipsis text-base font-normal text-tertiary ">
          {description}
        </TextOverflowTooltip>
      )}
    </h2>
  );
}

export function DetailPageTitle({
  title,
  description,
  resourceType,
  id,
  isLoading,
  leftDecorator,
  rightDecorator,
  maxWidth,
}: {
  title: ReactNode;
  description?: ReactNode;
  resourceType: ResourceType;
  id?: string;
  isLoading?: boolean;
  leftDecorator?: ReactNode;
  rightDecorator?: ReactNode;
  maxWidth?: string;
}) {
  const icon = ICON_MAP(cn('size-4'))[resourceType];

  return (
    <div
      className="flex items-center gap-2"
      style={maxWidth ? { maxWidth } : undefined}
    >
      {leftDecorator}
      {icon}

      {isLoading ? (
        <Skeleton className="m-0 h-6 w-40" />
      ) : (
        <div className="min-w-0 flex-1">
          <TextOverflowTooltip textVariant="h3">{title}</TextOverflowTooltip>
        </div>
      )}
      {id && (
        <CopyInlineLink value={id} hideIcon>
          <Badge color="secondary" leftDecorator={Link01Icon} size="sm">
            ID
          </Badge>
        </CopyInlineLink>
      )}
      {description && (
        <Tooltip title={description}>
          <div>
            <InfoCircleIcon className="size-4 stroke-[1.5px]" />
          </div>
        </Tooltip>
      )}
      {rightDecorator}
    </div>
  );
}
